import { useState } from 'react';
import { ChevronDown, ChevronUp } from '@drift-labs/icons';
import Divider from 'src/components/primitives/Divider/Divider';
import Switch from 'src/components/primitives/Switch/Switch';
import NotificationSettingsRow from './NotificationSettingsRow';
import { useNotificationSettings } from './hooks/useNotificationSettings';
import { SettingsView } from './types';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

interface NotificationSettingsProps {
	onNavigate: (view: SettingsView) => void;
}

export default function NotificationSettings({
	onNavigate,
}: NotificationSettingsProps) {
	const { settings, toggleSetting } = useNotificationSettings();
	const [alertsExpanded, setAlertsExpanded] = useState(true);
	const [marketsExpanded, setMarketsExpanded] = useState(true);
	const isMobile = useIsMobileScreenSize();

	return (
		<div className="flex flex-col gap-lg">
			{/* Alerts Section */}
			<div className="flex flex-col gap-lg">
				<button
					className="flex w-full items-center gap-lg"
					onClick={() => setAlertsExpanded(!alertsExpanded)}
				>
					<span className="flex-1 text-left typo-t3 text-text-default">
						Alerts
					</span>
					{alertsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
				</button>

				{alertsExpanded && (
					<>
						<NotificationSettingsRow
							label="Liquidation Alert"
							checked={settings.liquidationAlert}
							onCheckedChange={() => toggleSetting('liquidationAlert')}
						/>
						<NotificationSettingsRow
							label="Liquidation Warning"
							checked={settings.liquidationWarning}
							onCheckedChange={() => toggleSetting('liquidationWarning')}
						/>
						<NotificationSettingsRow
							label="Order Triggered"
							checked={settings.orderTriggered}
							onCheckedChange={() => toggleSetting('orderTriggered')}
						/>
						<NotificationSettingsRow
							label="Order Filled"
							checked={settings.orderFilled}
							onCheckedChange={() => toggleSetting('orderFilled')}
						/>
						<NotificationSettingsRow
							label="Position Closed"
							checked={settings.positionClosed}
							onCheckedChange={() => toggleSetting('positionClosed')}
						/>
					</>
				)}
			</div>

			<Divider />

			{/* Markets Section */}
			<div className="flex flex-col gap-lg">
				<button
					className="flex w-full items-center gap-lg"
					onClick={() => setMarketsExpanded(!marketsExpanded)}
				>
					<span className="flex-1 text-left typo-t3 text-text-default">
						Markets
					</span>
					{marketsExpanded ? (
						<ChevronUp size={18} />
					) : (
						<ChevronDown size={18} />
					)}
				</button>

				{marketsExpanded && (
					<>
						<NotificationSettingsRow
							label="Funding Rate Alert"
							navigable
							onNavigate={() =>
								onNavigate({
									screen: 'fundingRateAlertList',
								})
							}
						/>
						<NotificationSettingsRow
							label="Delist Notice"
							checked={settings.delistNotice}
							onCheckedChange={() => toggleSetting('delistNotice')}
						/>
						<NotificationSettingsRow
							label="New Market"
							checked={settings.newMarket}
							onCheckedChange={() => toggleSetting('newMarket')}
						/>
					</>
				)}
			</div>

			<Divider />

			{/* News and Announcements */}
			<div className="flex w-full items-center gap-lg">
				<span className="flex-1 typo-t3 text-text-default">
					News and Announcements
				</span>
				<Switch
					size="sm"
					checked={settings.newsAndAnnouncements}
					onCheckedChange={() => toggleSetting('newsAndAnnouncements')}
				/>
			</div>

			{/* Mobile Push Notification Settings */}
			{isMobile && (
				<>
					<Divider />
					<div className="flex flex-col gap-lg">
						<span className="typo-t3 text-text-default">
							Push Notifications
						</span>
						<div className="flex w-full items-center gap-lg">
							<span className="flex-1 typo-b4 text-text-secondary">
								Enable Push Notifications
							</span>
							<Switch size="sm" checked={false} />
						</div>
					</div>
				</>
			)}
		</div>
	);
}
